import { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";

import { cn } from "@helpers/general";
import useDeviceDetect from "@hooks/useDeviceDetect";
import { useLocalizedSentenceDict } from "@hooks/useSentenceDict";
import { useTracking } from "@context/Tracking";

import IconArrow from "@svgs/arrow-br.svg";
import { ComponentType } from "@constants/ComponentType";

const sortOptions = [
  { label: "Best sellers", value: "all" },
  { label: "New Arrivals", value: "new" },
  { label: "Prices: low to high", value: "low_high" },
  { label: "Prices: high to low", value: "high_low" },
];

function SortDropdown({ sortingType, onSort, componentType }) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const dict = useLocalizedSentenceDict();

  const { trackCollectionSortClick } = useTracking();
  const { isMobile } = useDeviceDetect();

  const handleOnSort = (value) => {
    onSort(value);
    trackCollectionSortClick(value);
  };

  const toggleDropdown = () => {
    setIsOpen((prevIsOpen) => !prevIsOpen);
  };

  const handleClickOutside = (event) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
      setIsOpen(false);
    }
  };

  const handleOptionSelect = (optionValue) => {
    handleOnSort(optionValue);
    setIsOpen(false);
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <div
      className={cn("relative", { "mobile:hidden": componentType === ComponentType.CP_ADS })}
      ref={dropdownRef}
    >
      <button
        type="button"
        className="flex justify-between items-center gap-[10px] typo-body"
        onClick={toggleDropdown}
      >
        <span>
          {dict.get("Sort By")}
          {sortingType !== "all" && sortingType ? (
            <>
              {": "}
              <span className="font-bold">
                {sortOptions.find((option) => option.value === sortingType)?.label}
              </span>
            </>
          ) : null}
        </span>
        <IconArrow
          width={isMobile ? "12" : "18"}
          height={isMobile ? "6" : "9"}
          className={isOpen && "rotate-180"}
        />
      </button>

      {isOpen && (
        <div
          className={cn(
            "absolute p-[20px] right-0 top-[30px] flex gap-[10px] flex-col items-start border border-black rounded-[4px] bg-white z-[100]",
            componentType === ComponentType.SEARCH
              ? "min-w-[166px] desktop:min-w-[200px]"
              : "min-w-[200px]"
          )}
        >
          {sortOptions.map((option) => (
            <button
              type="button"
              key={`custom_dropdown_${option.value}`}
              className={cn("typo-body", {
                underline: sortingType === option.value,
              })}
              onClick={() => handleOptionSelect(option.value)}
            >
              {option.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

SortDropdown.propTypes = {
  onSort: PropTypes.func.isRequired,
  sortingType: PropTypes.string,
  componentType: PropTypes.string,
};

export default SortDropdown;
